// src/pages/CartPage.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, Sparkles } from "lucide-react";
import { useCart } from "../Context/Cartcontext";

function FontImport() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;1,9..144,500&family=DM+Sans:wght@400;500;600;700&display=swap');
      .og-display{font-family:'Fraunces',serif}
      .og-body{font-family:'DM Sans',sans-serif}
    `}</style>
  );
}

// Payment logos
const PAYMENT_LOGOS = [
  { name:"Visa",       color:"#1A1F71", text:"VISA" },
  { name:"Mastercard", color:"#EB001B", text:"MC" },
  { name:"Amex",       color:"#007BC1", text:"AMEX" },
  { name:"RuPay",      color:"#097939", text:"RuPay" },
  { name:"UPI",        color:"#097939", text:"UPI" },
  { name:"GPay",       color:"#4285F4", text:"GPay" },
];

export default function CartPage() {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQty } = useCart();

  const subtotal = cartItems.reduce((s, i) => s + i.price * (i.qty || 1), 0);
  const totalItems = cartItems.reduce((s, i) => s + (i.qty || 1), 0);

  // Edit click hote hi user ko wapas usi product ke detail page pe le jaata hai
  const handleEdit = (item) => {
    if (item.basePath && item.slug) {
      navigate(`${item.basePath}/${item.slug}`);
    } else if (item.slug) {
      // fallback agar basePath store nahi hua purane cart items ke liye
      navigate(`/dailyplanners/${item.slug}`);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="og-body min-h-screen bg-white text-[#1A1A1A]">
        <FontImport/>
        {/* Announcement */}
        <div className="bg-[#FFEB3B] py-2.5 sm:py-3 px-4 text-center">
          <p className="og-display text-[13px] sm:text-sm italic font-medium">Get 10% off your first Photobook</p>
          <p className="text-[11px] sm:text-xs text-black/80 mt-0.5">300+ designs to personalise • No code required. <Link to="/photobook" className="font-bold underline">Shop now!</Link></p>
        </div>

        <div className="mx-auto max-w-3xl px-4 py-12 sm:py-20 text-center">
          <Link to="/" className="text-sm underline underline-offset-4 text-gray-500 hover:text-black block mb-6 sm:mb-8">Keep shopping</Link>
          <h1 className="og-display text-3xl sm:text-5xl italic mb-4 sm:mb-6">Your cart</h1>
          <p className="text-gray-500 mb-8">Your cart is empty.</p>
          <Link to="/" className="inline-block bg-[#1A1A1A] text-white rounded-full px-8 py-3 text-sm font-semibold hover:opacity-90">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="og-body min-h-screen bg-white text-[#1A1A1A]">
      <FontImport/>

      {/* Announcement */}
      <div className="bg-[#FFEB3B] py-2.5 sm:py-3 px-4 text-center">
        <p className="og-display text-[13px] sm:text-sm italic font-medium">Get 10% off your first Photobook</p>
        <p className="text-[11px] sm:text-xs text-black/80 mt-0.5">300+ designs to personalise • No code required. <Link to="/photobook" className="font-bold underline">Shop now!</Link></p>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-6 sm:py-10 sm:px-6">
        <Link to="/" className="text-sm underline underline-offset-4 text-gray-500 hover:text-black block mb-4">
          Keep shopping
        </Link>
        <h1 className="og-display text-3xl sm:text-5xl italic mb-5 sm:mb-8">Your cart</h1>

        {/* Made to order banner */}
        <div className="bg-[#EEF4F4] rounded-xl px-4 sm:px-6 py-4 mb-6 sm:mb-8 text-center max-w-2xl">
          <p className="text-sm font-semibold text-[#1A1A1A] mb-1">Our Products Are Made To Order</p>
          <p className="text-[13px] sm:text-sm text-gray-500 leading-relaxed">
            At Aishcraft, we print on demand which means we don't actually carry any stock — less waste and designs that are truly one of a kind.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-6 sm:gap-10 items-start">

          {/* Left — Cart items */}
        <div className="flex flex-col divide-y divide-gray-100 max-h-[600px] overflow-y-auto pr-2">
            {cartItems.map((item, idx) => (
              <div key={idx} className="flex gap-3 sm:gap-5 py-5 sm:py-6">
                {/* Image */}
                <div className="w-20 h-24 sm:w-28 sm:h-32 flex-shrink-0 rounded-lg overflow-hidden bg-gray-50 border border-gray-100">
                  <img src={item.image} alt={item.name} className="w-full h-full object-contain p-1.5 sm:p-2"/>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 sm:gap-4">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-[14px] sm:text-base leading-snug truncate sm:whitespace-normal">{item.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{item.subtitle}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-[14px] sm:text-base">Rs. {(item.price*(item.qty||1)).toLocaleString("en-IN")}</p>
                      {item.comparePrice && (
                        <p className="text-[11px] sm:text-xs text-gray-400 line-through">Rs. {(item.comparePrice*(item.qty||1)).toLocaleString("en-IN")}</p>
                      )}
                    </div>
                  </div>

                  {/* Personalised badge */}
                  {item.personalisedName && (
                    <div className="mt-2 inline-flex items-center gap-1.5 bg-[#FFEB3B] px-2.5 sm:px-3 py-1 rounded-full">
                      <Sparkles size={12} className="text-[#1A1A1A]"/>
                      <span className="text-[10px] sm:text-[11px] font-bold tracking-widest">PERSONALISED</span>
                    </div>
                  )}

                  {/* Attributes */}
                  <div className="mt-2 flex flex-col gap-0.5">
                    {item.personalisedName && <p className="text-[11px] sm:text-xs text-gray-500">Name: {item.personalisedName}</p>}
                    {item.paperType && <p className="text-[11px] sm:text-xs text-gray-500">Paper Type: {item.paperType}</p>}
                    {item.coverType && <p className="text-[11px] sm:text-xs text-gray-500">Cover: {item.coverType}</p>}
                    {item.color && <p className="text-[11px] sm:text-xs text-gray-500">Color: {item.color}</p>}
                    {item.size && <p className="text-[11px] sm:text-xs text-gray-500">Size: {item.size}</p>}
                    {item.format && <p className="text-[11px] sm:text-xs text-gray-500">Format: {item.format}</p>}
                  </div>

                  {/* Qty + Actions */}
                  <div className="mt-3 flex items-center flex-wrap gap-3 sm:gap-6">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 font-medium">Qty:</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQty ? updateQty(idx, Math.max(1, (item.qty||1)-1)) : null}
                          className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors">
                          <Minus size={10}/>
                        </button>
                        <span className="text-sm font-semibold w-4 text-center">{item.qty||1}</span>
                        <button
                          onClick={() => updateQty ? updateQty(idx, (item.qty||1)+1) : null}
                          className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors">
                          <Plus size={10}/>
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm">
                      
                      <button
                        onClick={() => removeFromCart(idx)}
                        className="underline underline-offset-2 text-gray-500 hover:text-red-500 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right — Order Summary */}
          <div className="lg:sticky lg:top-6">
            <div className="bg-[#F0F4F4] rounded-2xl p-5 sm:p-6 flex flex-col gap-4">
              <h2 className="text-lg font-bold">Order Summary</h2>

              <p className="text-sm text-gray-500">{totalItems} item{totalItems!==1?"s":""}</p>

              <div className="flex flex-col gap-3 border-t border-gray-200 pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold">Rs. {subtotal.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping & taxes</span>
                  <span className="text-gray-400 text-xs">calculated at checkout</span>
                </div>
              </div>

              <div className="flex justify-between items-baseline border-t border-gray-200 pt-4">
                <span className="font-bold text-base">Total</span>
                <span className="font-bold text-xl">Rs. {subtotal.toLocaleString("en-IN")}</span>
              </div>

              <button
                onClick={() => navigate("/checkout")}
                className="w-full bg-[#1A1A1A] text-white rounded-xl py-3.5 sm:py-4 text-sm font-bold hover:opacity-90 transition-opacity mt-2">
                Proceed to checkout
              </button>

              {/* We accept */}
              <div className="mt-2 text-center">
                <p className="text-xs text-gray-400 mb-3">We accept</p>
             
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}